import { Injectable } from '@angular/core';
import { IssuesTimeline, Resolution } from '../issue/issue.dto';
import { StatItem } from './warehouse.dto';
import { orderBy } from 'lodash-es';
import moment from 'moment';
import { logger, NO_LIMIT_SEARCH_PARAMS } from '@damofire/data-commons';
import { IssueService } from '../issue/issue.service';
import { Timestamp } from 'firebase/firestore';

export interface WarehouseStats {
  assets: number;
  risks: StatItem[];
  components: StatItem[];
  timeline?: IssuesTimeline;
}

@Injectable()
export class WarehouseStatsService {
  private static readonly MONTHS_IN_YEAR = 12;
  constructor(private readonly issueService: IssueService) {}

  private static statsObjToArray(stats: Record<string, number>): StatItem[] {
    return Object.keys(stats).map((key) => ({ key, doc_count: stats[key] }));
  }

  private static createMonthlyKey(date: string | Date): string {
    const actual: Date = typeof date === 'string' ? new Date(Date.parse(date)) : date;
    const month = actual.getMonth() + 1;
    const year = actual.getFullYear();
    return `${year}-${month}`;
  }

  private static monthlyKeyComparator(a: string, b: string): number {
    const previous = a.split('-');
    const current = b.split('-');
    let comp = previous[0].localeCompare(current[0]);

    if (comp === 0) {
      comp = parseInt(previous[1], 10) - parseInt(current[1], 10);
    }

    return comp;
  }

  private static sanitizeMonthlyData(stats: { [key: string]: number }): { [key: string]: number } {
    const sorted: Record<string, number> = {};
    const keys = Object.keys(stats);
    keys.sort(WarehouseStatsService.monthlyKeyComparator);
    logger.debug('Filling gaps in monthly data.', { keys: keys.join(','), category: 'warehouse' });

    keys.reduce((p, c) => {
      const previous = p.split('-');
      const current = c.split('-');

      const sameYear = previous[0].localeCompare(current[0]) === 0;
      const currentMonth = parseInt(current[1], 10);
      const previousMonth = parseInt(previous[1], 10);

      if (sameYear) {
        this.sanitizeSameYearData(currentMonth, previousMonth, stats, previous);
      } else {
        this.sanitizeDiffYearData(previousMonth, currentMonth, current, previous, stats);
      }

      return c;
    });

    const newKeys = Object.keys(stats);
    newKeys.sort(WarehouseStatsService.monthlyKeyComparator);
    newKeys.forEach((k) => (sorted[k] = stats[k]));

    return sorted;
  }

  private static sanitizeDiffYearData(previousMonth: number, currentMonth: number, current: string[], previous: string[], stats: { [p: string]: number }): void {
    const monthDiff = WarehouseStatsService.MONTHS_IN_YEAR - previousMonth + (currentMonth - 1);
    logger.debug('Missing months in list.', { monthDiff, category: 'warehouse' });

    for (let i = 0; i < monthDiff; ++i) {
      let nextMonth = previousMonth + i + 1;
      const year = nextMonth > WarehouseStatsService.MONTHS_IN_YEAR ? current[0] : previous[0];

      if (nextMonth > WarehouseStatsService.MONTHS_IN_YEAR) {
        nextMonth = nextMonth % WarehouseStatsService.MONTHS_IN_YEAR;
      }

      if (!stats[`${year}-${nextMonth}`]) {
        stats[`${year}-${nextMonth}`] = 0;
      }
      logger.debug('Added month entry.', { key: `${year}-${nextMonth}`, month: nextMonth, category: 'warehouse' });
    }
  }

  private static sanitizeSameYearData(currentMonth: number, previousMonth: number, stats: { [p: string]: number }, previous: string[]): void {
    const monthDiff = currentMonth - previousMonth;
    if (monthDiff > 1) {
      logger.debug('Missing months in list.', { monthDiff, category: 'warehouse' });

      for (let i = 0; i < monthDiff; ++i) {
        if (!stats[`${previous[0]}-${previousMonth + i + 1}`]) {
          stats[`${previous[0]}-${previousMonth + i + 1}`] = 0;
        }
      }
    }
  }

  private static incrementIfInRange(dateField: Date | Timestamp, firstDayOfEndPeriod: moment.Moment, startOfPeriod: moment.Moment, destination: { [key: string]: number }): void {
    if (!dateField) {
      return;
    }

    try {
      const dateValue = dateField instanceof Date ? dateField : dateField.toDate();
      if (moment(dateValue).isBetween(firstDayOfEndPeriod, startOfPeriod, 'day', '[]')) {
        const key = WarehouseStatsService.createMonthlyKey(dateValue);
        destination[key] = (destination[key] || 0) + 1;
      }
    } catch (e) {
      logger.error('dateField is not a date or timestamp.', { dateField, category: 'warehouse', error: e });
    }
  }

  async getStats(divisionId: string, inspectionId?: string): Promise<WarehouseStats> {
    const assetStats: Record<string, number> = {};
    const riskStats: Record<string, number> = {};
    const componentStats: Record<string, number> = {};
    const filters = inspectionId ? { 'inspection._id': +inspectionId } : {};

    const repaired: Record<string, number> = {};
    const created: Record<string, number> = {};
    const startOfPeriod = moment();
    const endOfPeriod = moment().subtract(12, 'months').startOf('month');

    if (!inspectionId) {
      repaired[WarehouseStatsService.createMonthlyKey(startOfPeriod.toDate())] = 0;
      repaired[WarehouseStatsService.createMonthlyKey(endOfPeriod.toDate())] = 0;
      created[WarehouseStatsService.createMonthlyKey(startOfPeriod.toDate())] = 0;
      created[WarehouseStatsService.createMonthlyKey(endOfPeriod.toDate())] = 0;
    }

    const issues = (await this.issueService.search(divisionId, { filters, ...NO_LIMIT_SEARCH_PARAMS })).result;
    issues.forEach((issue) => {
      if (inspectionId || issue.resolution === Resolution.OPEN) {
        assetStats[issue.asset._id] = (assetStats[issue.asset._id] || 0) + 1;
        const riskDescription = issue.riskLevel?.description || 'UNKNOWN';
        riskStats[riskDescription] = (riskStats[riskDescription] || 0) + 1;
        componentStats[issue.component.description] = (componentStats[issue.component.description] || 0) + 1;
      }

      if (!inspectionId) {
        WarehouseStatsService.incrementIfInRange(issue.repair?.date, endOfPeriod, startOfPeriod, repaired);
        WarehouseStatsService.incrementIfInRange(issue.audit.createdDate, endOfPeriod, startOfPeriod, created);
      }
    });

    const stats: WarehouseStats = {
      assets: Object.keys(assetStats).length,
      risks: WarehouseStatsService.statsObjToArray(riskStats),
      components: orderBy(WarehouseStatsService.statsObjToArray(componentStats), ['doc_count'], ['desc']),
    };

    if (!inspectionId) {
      stats.timeline = {
        repaired: WarehouseStatsService.sanitizeMonthlyData(repaired),
        created: WarehouseStatsService.sanitizeMonthlyData(created),
      };
    }

    return stats;
  }
}
